/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  split,
} from '@apollo/client';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { ApolloProvider } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import { useAuth0 } from '@auth0/auth0-react';
import { showNotification } from '@mantine/notifications';

// Types
import { PropsWithChildren, useEffect, useMemo } from 'react';
import { getMainDefinition } from '@apollo/client/utilities';
import fragmentMatcher from '@app-types/blackroom-api/fragment-matcher';
import i18n from '@services/translation/TranslationsProvider/i18n';
import {
  getViewAsConnectionParams,
  getViewAsRequestHeaders,
} from '@app/shared/providers/ViewAsProvider/store';
import { registerWsClient } from './wsClientRegistry';
import { createInvitationLink } from '@services/invitation';

// Backend error code for mutations rejected during a view-as session.
const VIEW_AS_FORBIDDEN_MUTATION_CODE = 'VIEW_AS_MUTATION_FORBIDDEN';

/* -------------------------------------------------------------------------- */
/*                                   Modules                                  */
/* -------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function ApolloWrapper({ children }: PropsWithChildren) {
  /* ********************************** HOOKS ********************************* */

  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  /* ***************************** LOCAL VARIABLES **************************** */

  const wsClient = useMemo(
    () =>
      createClient({
        url: import.meta.env.VITE_API_CORE_GRAPHQL_WS_URL || '',
        connectionParams: async () => {
          try {
            const token = await getAccessTokenSilently();
            return {
              authorization: `Bearer ${token}`,
              credentials: 'same-origin',
              ...getViewAsConnectionParams(),
            };
          } catch {
            return { credentials: 'same-origin' };
          }
        },
      }),
    [getAccessTokenSilently],
  );

  useEffect(() => {
    registerWsClient(wsClient);
    return () => registerWsClient(null);
  }, [wsClient]);

  const client = useMemo(() => {
    const cache = new InMemoryCache({
      addTypename: true,
      possibleTypes: fragmentMatcher.possibleTypes,
    });

    const httpLink = new HttpLink({
      uri: import.meta.env.VITE_API_CORE_GRAPHQL_URL,
      credentials: 'same-origin',
    });

    const wsLink = new GraphQLWsLink(wsClient);

    const authLink = setContext(async (_, { headers }) => {
      try {
        const token = await getAccessTokenSilently();
        return {
          headers: {
            ...headers,
            authorization: token ? `Bearer ${token}` : '',
          },
        };
      } catch {
        // Force a redirect-based login flow to obtain a fresh refresh token.
        // Iframe-based silent auth (session cookie) doesn't return refresh tokens,
        // so only a full redirect to Auth0's /authorize endpoint will fix this.
        await loginWithRedirect();
        return { headers };
      }
    });

    const splitLink = split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return (
          definition.kind === 'OperationDefinition' &&
          definition.operation === 'subscription'
        );
      },
      wsLink,
      httpLink,
    );

    const errorLink = onError(({ graphQLErrors, networkError }) => {
      if (graphQLErrors) {
        graphQLErrors.forEach(({ message, locations, path }) =>
          console.error(
            `[apollo-client]: GraphQL error: Message: ${message}, Location: ${locations}, Path: ${path}`,
          ),
        );

        const hasUnauthorized = graphQLErrors.some(
          ({ message }) => message === 'Unauthorized',
        );
        if (hasUnauthorized) {
          loginWithRedirect();
        }

        const hasViewAsMutationBlocked = graphQLErrors.some(
          ({ extensions }) =>
            extensions?.code === VIEW_AS_FORBIDDEN_MUTATION_CODE,
        );
        if (hasViewAsMutationBlocked) {
          showNotification({
            title: i18n.t('User:ViewAs.errors.mutationBlocked.title'),
            message: i18n.t('User:ViewAs.errors.mutationBlocked.message'),
            color: 'blackroomOrange',
          });
        }
      }
      if (networkError) {
        console.error(`[apollo-client]: Network error: ${networkError}`);
      }
    });

    const viewAsLink = new ApolloLink((operation, forward) => {
      operation.setContext(({ headers = {} }) => ({
        headers: {
          ...headers,
          ...getViewAsRequestHeaders(),
        },
      }));
      return forward(operation);
    });

    const operationNameLink = new ApolloLink((operation, forward) => {
      operation.setContext(({ headers = {} }) => ({
        headers: {
          ...headers,
          'x-graphql-operation-name': operation.operationName,
        },
      }));
      return forward(operation);
    });

    const link = ApolloLink.from([
      operationNameLink,
      viewAsLink,
      createInvitationLink({
        // A full redirect: the Auth0 session is fresh, so no password prompt.
        onSessionRenewalRequired: () => loginWithRedirect(),
      }),
      errorLink,
      authLink,
      splitLink,
    ]);

    return new ApolloClient({
      link,
      connectToDevTools: true,
      cache,
    });
  }, [wsClient, getAccessTokenSilently, loginWithRedirect]);

  /* ******************************** RENDERING ******************************* */
  return <ApolloProvider client={client}>{children}</ApolloProvider>;
}

export default ApolloWrapper;
